/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1786665600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Aug 14 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "freewheel-data-suite",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to FreeWheel Data Suite",
          "depth": 0,
          "id": "connect-to-freewheel-data-suite"
        }, {
          "value": "Deliver to a custom bucket",
          "depth": 1,
          "id": "deliver-to-a-custom-bucket"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Audiences",
          "depth": 1,
          "id": "audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      pre: "pre",
      span: "span",
      h4: "h4"
    }, _provideComponents(), props.components), {MatchBoosterDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["FreeWheel Data Suite is ", _jsx(_components.a, {
          href: "https://www.freewheel.com/",
          children: "FreeWheel's"
        }), " data activation offering for its TV platform. With the FreeWheel Data Suite destination, you can create segments in FreeWheel's Audience Manager and keep their membership in sync with your warehouse data, reaching viewers across streaming and connected TV inventory. If your model doesn't already contain FreeWheel-supported identifiers, you can enrich it with them using Match Booster."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch is a registered FreeWheel audience partner and delivers your data through FreeWheel's Audience Manager S3 ingestion pipeline. You don't need to provide AWS credentials or run any infrastructure of your own."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#audiences",
                children: _jsx(_components.strong, {
                  children: "Audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Create FreeWheel segments and manage their membership"
            }), _jsx(_components.td, {
              children: "All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.freewheel.com/news/freewheel-introduces-new-solution-to-enable-publishers-to-unlock-faster-audience-activation-across-screens",
                children: "Learn about Audience Manager ↗"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.p, {
        children: "Between runs, Hightouch computes which users entered or left your audience and delivers those changes to FreeWheel as incremental additions and removals. FreeWheel's detailed ingestion specification isn't publicly hosted; FreeWheel distributes it to partners during onboarding."
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-freewheel-data-suite",
        children: "Connect to FreeWheel Data Suite"
      }), "\n", _jsx(_components.p, {
        children: "Before connecting, ask your FreeWheel account manager to enable audience delivery from Hightouch for your network. FreeWheel provisions your network ID for both its staging and production ingestion environments, and FreeWheel recommends validating your first syncs on staging before switching to production."
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "FreeWheel Data Suite"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Environment"
          }), ": Which FreeWheel ingestion environment to deliver to, ", _jsx(_components.strong, {
            children: "Staging"
          }), " or ", _jsx(_components.strong, {
            children: "Production"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client network ID"
          }), ": Your FreeWheel network ID, which FreeWheel provides during onboarding. Use your own network ID for first-party data, or ", _jsx(_components.code, {
            children: "-1"
          }), " to make segments available to all networks as third-party data. Staging and production use different network IDs, so update this field when you switch environments."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data provider name"
          }), ": An optional friendly name recorded as the data provider on the segments you create, typically your company name. If left blank, FreeWheel attributes segments to Hightouch as the delivering audience partner."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "deliver-to-a-custom-bucket",
        children: "Deliver to a custom bucket"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch delivers files directly to FreeWheel's ingestion buckets for the environment you selected. If your FreeWheel arrangement instead ingests from a different location, such as an S3 bucket your company owns or a secondary bucket FreeWheel has assigned you, enter that bucket name or S3 Access Point ARN as the ", _jsx(_components.strong, {
          children: "Bucket override"
        }), " under ", _jsx(_components.strong, {
          children: "Advanced"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "When delivering to a bucket your company owns, grant write access to Hightouch's delivery role by adding a statement like this to your bucket policy:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"AllowHightouchFreeWheelDelivery\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Principal\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"AWS\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:iam::010438485252:role/freewheel-datashare\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:PutObject\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:GetObject\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:ListBucket\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::your-bucket-name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::your-bucket-name/audience_partner=hightouch/*\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch writes all files under the ", _jsx(_components.code, {
          children: "audience_partner=hightouch/"
        }), " prefix, following the same layout FreeWheel's ingestion expects. Coordinate with FreeWheel to confirm they ingest from your bucket before relying on this setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your FreeWheel Data Suite destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the FreeWheel Data Suite destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "audiences",
        children: "Audiences"
      }), "\n", _jsx(_components.p, {
        children: "Create a FreeWheel segment and keep its membership in sync with your model, or manage the membership of a segment that already exists in FreeWheel."
      }), "\n", _jsx(_components.h4, {
        id: "create-a-new-segment",
        children: "Create a new segment"
      }), "\n", _jsxs(_components.p, {
        children: ["Creating a new segment is the most common configuration. Hightouch registers the segment with FreeWheel on every sync run, so its definition in FreeWheel always tracks your sync configuration. New segments are created with the ID ", _jsx(_components.code, {
          children: "hightouch-sync-<your sync's ID>"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Configure the following segment attributes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Segment name"
          }), ": If left blank, Hightouch uses the name of your model or audience. You can customize the name with templating, like ", _jsx(_components.code, {
            children: "Audience_{model.id}"
          }), ", and convey hierarchy with ", _jsx(_components.code, {
            children: ">"
          }), " separators, for example ", _jsx(_components.code, {
            children: "Provider > Category > Segment"
          }), ". See available variables in the ", _jsx(_components.a, {
            href: "/destinations/s3#enter-filename",
            children: "filename templating docs"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Segment description"
          }), ": An optional description of the segment in business terms."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "CPM price"
          }), ": The CPM price advertisers pay to use this segment. Leave blank for first-party segments that only their owner uses."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Time to live (TTL) in days"
          }), ": How many days segment members persist in Audience Manager after ingestion before expiring, from 0 to 90. If left blank, FreeWheel applies the default TTL configured for your integration."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can also set optional taxonomy attributes. Leave these unset unless FreeWheel has instructed you to use them:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use restrictions"
          }), ": Any value here prevents the segment from showing in FreeWheel's UI, for segments subject to data-policy restrictions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom"
          }), ": Mark the segment as a custom or first-party segment rather than syndicated."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "SPI Indicator"
          }), ": Flag the segment as containing sensitive personal information."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Federated"
          }), ": Mark the segment as federated."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Geo"
          }), ": For geo segments only, how FreeWheel should interpret the segment's external ID: zip code, postal code, or country."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "FreeWheel doesn't allow the SPI flag to be removed from a segment once it's\nset. Only enable it when the segment genuinely contains sensitive personal\ninformation."
        })
      }), "\n", _jsx(_components.h4, {
        id: "sync-to-an-existing-segment",
        children: "Sync to an existing segment"
      }), "\n", _jsx(_components.p, {
        children: "To manage the membership of a segment that already exists in FreeWheel, select the existing segment option and enter its FreeWheel segment ID. Hightouch only delivers membership changes for existing segments; it never modifies the segment's name, pricing, TTL, or other attributes, so a segment created outside Hightouch stays exactly as its owner configured it."
      }), "\n", _jsx(_components.h4, {
        id: "map-id-types",
        children: "Map ID types"
      }), "\n", _jsx(_components.p, {
        children: "Select one or more FreeWheel ID types to sync and map each to a column in your model:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "AAID (Android)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "IDFA (Apple)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "CTV device ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "IPv4 address"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "IPv6 address"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "FreeWheel cookie"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Experian ID (LUID)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "LiveRamp ID (IDL)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Tapad household ID"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch delivers one membership file per ID type on each sync run. Columns containing arrays of identifiers are expanded into one identifier per row, so a single user can contribute several device IDs. All streaming device IDs, such as Roku, Fire TV, Samsung Tizen, and Apple TV, are delivered under the single CTV device ID type."
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "FreeWheel Data Suite"
      }), "\n", _jsx(_components.h4, {
        id: "configure-delete-behavior",
        children: "Configure delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["Audience syncs run in All mode: users who leave your audience are automatically delivered to FreeWheel as removals on the next run. Under ", _jsx(_components.strong, {
          children: "Advanced"
        }), ", you can also set a full resync interval, which schedules a periodic full refresh that re-delivers your entire audience. This is useful as a safety net in case FreeWheel's copy of the segment ever drifts from your model, for example after a segment reset on FreeWheel's side."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to FreeWheel Data Suite, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error\ncodes: Other destinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, verify the results in FreeWheel:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Find the segment in Audience Manager. Segments created by Hightouch have the ID ", _jsx(_components.code, {
            children: "hightouch-sync-<your sync's ID>"
          }), " and the name you configured, or your model's name if you left the name blank."]
        }), "\n", _jsx(_components.li, {
          children: "Confirm the membership count is in the range you expect once FreeWheel finishes ingesting. Allow time for FreeWheel's ingestion to process newly delivered files before concluding that data is missing."
        }), "\n", _jsx(_components.li, {
          children: "If you started on the staging environment, repeat the validation after switching the destination to production. Remember that production uses a different client network ID."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If nothing arrives, confirm with your FreeWheel account manager that audience delivery from Hightouch is enabled for your network ID in the environment you selected."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
